import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db, storage } from "../firbase/firebase";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";
import {
  updateStatsOnCreate,
  updateStatsOnDelete,
} from "../page/admin/dashboard/StatsService";

const COLLECTION = "thoughtArticles";

//! helper function upload image to storage
const uploadArticleImage = async (file) => {
  const imageRef = ref(storage, `article-images/${Date.now()}-${file.name}`);

  await uploadBytes(imageRef, file);
  return await getDownloadURL(imageRef);
};

export const addArticle = createAsyncThunk(
  "articles/addArticle",
  async (articleData, { rejectWithValue }) => {
    try {
      const createdAt = serverTimestamp();

      let imageUrl = "";

      if (articleData.image instanceof File) {
        imageUrl = await uploadArticleImage(articleData.image);
      }

      const payload = {
        ...articleData,
        image: imageUrl,
        createdAt,
      };

      delete payload.preview;

      const docRef = await addDoc(collection(db, COLLECTION), payload);

      await updateStatsOnCreate("articles", payload.status);

      return { id: docRef.id, ...payload };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

/* =========================
   FETCH ARTICLES
========================= */
export const fetchArticles = createAsyncThunk(
  "articles/fetchArticles",
  async (_, { rejectWithValue }) => {
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTION));
      const articles = [];

      querySnapshot.forEach((doc) => {
        articles.push({ id: doc.id, ...doc.data() });
      });

      return articles;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const updateArticle = createAsyncThunk(
  "articles/updateArticle",
  async ({ id, articleData }, { rejectWithValue }) => {
    try {
      if (!id) throw new Error("Invalid article ID");

      const updatedAt = serverTimestamp();

      let imageUrl = articleData.preview;

      if (articleData.image instanceof File) {
        if (articleData.preview) {
          try {
            const oldImageRef = ref(articleData.preview);
            await deleteObject(oldImageRef);
          } catch {}
        }
        imageUrl = await uploadArticleImage(articleData.image);
      }

      const payload = {
        title: articleData.title,
        subtitle: articleData.subtitle,
        author: articleData.author,
        designation: articleData.designation,
        category: articleData.category,
        status: articleData.status,
        content: articleData.content,
        image: imageUrl,
        updatedAt,
        createdAt: articleData.createdAt,
      };

      const articleRef = doc(db, COLLECTION, id);
      await setDoc(articleRef, payload, { merge: true });

      payload.status === "published"
        ? await updateStatsOnCreate("articles", payload.status)
        : await updateStatsOnDelete(
            "articles",
            payload.createdAt,
            payload.status,
          );

      return { id, ...payload };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  },
);

/* =========================
   DELETE ARTICLE
========================= */

export const deleteArticle = createAsyncThunk(
  "articles/deleteArticle",
  async ({ id, imageUrl }, { rejectWithValue }) => {
    try {
      if (imageUrl) {
        const imageRef = ref(storage, imageUrl);
        await deleteObject(imageRef);
      }

      const snap = await getDoc(doc(db, COLLECTION, id));
      const realData = snap.data();

      await updateStatsOnDelete(
        "articles",
        realData.createdAt,
        realData.status,
      );

      await deleteDoc(doc(db, COLLECTION, id));

      // await updateStatsOnDelete("articles", createdAt);

      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

/* =========================
   ARTICLE SLICE
========================= */
export const ArticleSlice = createSlice({
  name: "articles",
  initialState: {
    articles: [],
    loading: {
      fetch: false,
      add: false,
      update: false,
      delete: false,
    },
    error: null,
    editArticle: null,
  },
  reducers: {
    setEditArticleData: (state, action) => {
      state.editArticle = action.payload;
    },
    clearEditingArticle: (state) => {
      state.editArticle = null;
    },
  },
  extraReducers: (builder) => {
    builder
      /* add article */
      .addCase(addArticle.pending, (state) => {
        state.loading.add = true;
      })
      .addCase(addArticle.fulfilled, (state, action) => {
        state.loading.add = false;
        state.articles.push(action.payload);
      })
      .addCase(addArticle.rejected, (state, action) => {
        state.loading.add = false;
        state.error = action.payload;
      })

      /* fetch articles */
      .addCase(fetchArticles.pending, (state) => {
        state.loading.fetch = true;
        state.error = null;
      })
      .addCase(fetchArticles.fulfilled, (state, action) => {
        state.loading.fetch = false;
        state.articles = action.payload;
      })
      .addCase(fetchArticles.rejected, (state, action) => {
        state.loading.fetch = false;
        state.error = action.payload;
      })

      /* update article */
      .addCase(updateArticle.pending, (state) => {
        state.loading.update = true;
        state.error = null;
      })
      .addCase(updateArticle.fulfilled, (state, action) => {
        state.loading.update = false;
        const index = state.articles.findIndex(
          (article) => article.id === action.payload.id,
        );
        if (index !== -1) {
          state.articles[index] = {
            ...state.articles[index],
            ...action.payload,
          };
        }
        state.editArticle = null;
      })
      .addCase(updateArticle.rejected, (state, action) => {
        state.loading.update = false;
        state.error = action.payload;
      })

      /* delete article */
      .addCase(deleteArticle.pending, (state) => {
        state.loading.delete = true;
      })
      .addCase(deleteArticle.fulfilled, (state, action) => {
        state.loading.delete = false;
        state.articles = state.articles.filter(
          (article) => article.id !== action.payload,
        );
      })
      .addCase(deleteArticle.rejected, (state, action) => {
        state.loading.delete = false;
        state.error = action.payload;
      });
  },
});

export const { setEditArticleData, clearEditingArticle } = ArticleSlice.actions;

export default ArticleSlice.reducer;
